/**
 * Location review-completion helpers.
 *
 * Covers the fields a supplier must provide before a location can transition
 * from `DRAFT` to `IN_REVIEW`: the backend's `validateRequiredFields` set, plus
 * **Description** and **Capacity**, which the Create/Edit Location wizard
 * requires on its Location Settings step.
 *
 * This is deliberately a *subset* of the wizard's full rule set. The wizard also
 * enforces validity and cross-field rules — a 10-digit phone, a geocoded
 * address, a daily price for managed locations, auto-approve spaces not
 * exceeding capacity — that are about correctness rather than absence, and so
 * cannot be expressed as a "missing field". Treat `isComplete` as "no required
 * field is blank", not as "the wizard will accept this".
 *
 * **Rate** is satisfied by at least one positive price (daily or monthly) —
 * mirroring the backend go-live check — and **Auto-Approve Spaces** is only
 * required when automated approvals are enabled.
 *
 * Conditional fields are excluded from both the numerator and denominator, so the
 * percentage is always based on the correct applicable total.
 *
 * The logic is intentionally pure and decoupled from `components/`: it accepts a
 * minimal {@link LocationReviewFields} shape (all fields optional/nullable) so it
 * can be reused across multiple scenarios — e.g. the Draft card progress bar, the
 * Create/Edit Location wizard's "Submit for Review" enablement, or any future
 * review-gating UI. The full `Location` type structurally satisfies this shape,
 * as does partial wizard data.
 */

/**
 * Minimal shape needed to evaluate review completion. Every field is optional and
 * nullable so partially-filled drafts (and in-progress wizard state) can be passed
 * directly. The full `Location` type structurally satisfies this interface.
 */
export interface LocationReviewFields {
  locationName?: string | null;
  phoneNumber?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  /** Buyer-facing blurb; required by the wizard's Location Settings step. */
  locationDescription?: string | null;
  /** Total yard capacity. Must be greater than 0. */
  totalNumberOfSpaces?: number | null;
  /**
   * In cents. Counts toward the location's required "Rate": a location needs at
   * least one positive price — `pricePerDay` or `pricePerMonth` — to go live.
   * `0` or `null` means this price is unset.
   */
  pricePerDay?: number | null;
  /**
   * In cents. Counts toward the location's required "Rate" alongside
   * `pricePerDay`; at least one of the two must be positive. `0` or `null` means
   * this price is unset.
   */
  pricePerMonth?: number | null;
  /** Whether automated approvals are enabled for the location. */
  managedLocation?: boolean | null;
  /** Must be greater than 0 when automated approvals are enabled. */
  managedSpaces?: number | null;
  /**
   * At least one equipment type must be selected. Supplied as the full list by
   * the wizard, or as a count by the location-list VO, which is flat and does
   * not carry the rows. Either satisfies the requirement.
   */
  locationEquipmentTypes?: unknown[] | null;
  /** See {@link locationEquipmentTypes}. */
  equipmentTypeCount?: number | null;
}

/** Result of {@link getLocationReviewCompletion}. */
export interface LocationReviewCompletion {
  /** Whole-number percentage (0–100) of required fields that are complete. */
  completionPercent: number;
  /** Human-readable labels of the required fields that are still missing. */
  missingFields: string[];
  /**
   * `true` when every applicable required field is complete. Use this (not a
   * `completionPercent === 100` check) to gate correctness-critical behavior
   * such as enabling "Submit for Review" — `completionPercent` is a rounded
   * value intended for display and should never be relied on for exact
   * equality checks.
   */
  isComplete: boolean;
}

const isNonBlank = (value?: string | null): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const isPositiveNumber = (value?: number | null): boolean =>
  typeof value === 'number' && value > 0;

const hasAtLeastOne = (value?: unknown[] | null): boolean =>
  Array.isArray(value) && value.length > 0;

/**
 * Returns `true` when a location has at least one equipment type, reading
 * whichever representation the caller supplied. A caller that provides neither
 * is treated as having none.
 */
const hasEquipmentTypes = (loc: LocationReviewFields): boolean =>
  hasAtLeastOne(loc.locationEquipmentTypes) || isPositiveNumber(loc.equipmentTypeCount);

/**
 * Returns `true` when a location has at least one positive rate (daily or
 * monthly). This mirrors the backend go-live rule, which requires at least one of
 * `pricePerDay` or `pricePerMonth` to be present.
 */
const hasRate = (loc: LocationReviewFields): boolean =>
  isPositiveNumber(loc.pricePerDay) || isPositiveNumber(loc.pricePerMonth);

const isManagedLocation = (loc: LocationReviewFields): boolean => loc.managedLocation === true;

interface ReviewField {
  label: string;
  /** Returns `true` when the field is satisfied for the given location. */
  isComplete: (loc: LocationReviewFields) => boolean;
  /** When true, only include this field for managed locations. */
  requiresManagedLocation?: boolean;
}

/**
 * All fields that may be required to submit a location for review. Fields marked
 * conditional requirements are excluded from the applicable set when their
 * prerequisite is disabled, so they never penalise the completion percentage.
 * Order determines how missing fields are listed to the user.
 */
const REVIEW_REQUIRED_FIELDS: ReadonlyArray<ReviewField> = [
  { label: 'Name',              isComplete: (loc) => isNonBlank(loc.locationName) },
  { label: 'Phone Number',      isComplete: (loc) => isNonBlank(loc.phoneNumber) },
  { label: 'Street Address',    isComplete: (loc) => isNonBlank(loc.addressLine1) },
  { label: 'City',              isComplete: (loc) => isNonBlank(loc.city) },
  { label: 'State',             isComplete: (loc) => isNonBlank(loc.state) },
  { label: 'Zip',               isComplete: (loc) => isNonBlank(loc.zip) },
  { label: 'Description',       isComplete: (loc) => isNonBlank(loc.locationDescription) },
  { label: 'Capacity',          isComplete: (loc) => isPositiveNumber(loc.totalNumberOfSpaces) },
  { label: 'Rate',              isComplete: (loc) => hasRate(loc) },
  { label: 'Auto-Approve Spaces', isComplete: (loc) => isPositiveNumber(loc.managedSpaces), requiresManagedLocation: true },
  { label: 'Equipment Types',   isComplete: (loc) => hasEquipmentTypes(loc) },
];

/**
 * Evaluates how close a location is to being submittable for review.
 *
 * The applicable field set is dynamic: "Auto-Approve Spaces" is only included when
 * automated approvals are enabled (`managedLocation === true`). "Rate" is always
 * required and is satisfied by a positive daily OR monthly price.
 *
 * @param location - Any object with the review-relevant fields (full `Location`,
 *   partial draft, or wizard state).
 * @returns The completion percentage (0–100, rounded), the labels of any
 *   still-missing required fields in display order, and an exact `isComplete`
 *   boolean for gating logic that must not depend on rounded percentages.
 *
 * @example
 * const { completionPercent, missingFields, isComplete } = getLocationReviewCompletion(location);
 * // completionPercent: 78, missingFields: ['Rate', 'Equipment Types'], isComplete: false
 */
export function getLocationReviewCompletion(location: LocationReviewFields): LocationReviewCompletion {
  const managedLocation = isManagedLocation(location);

  const applicableFields = REVIEW_REQUIRED_FIELDS.filter(
    (field) => !field.requiresManagedLocation || managedLocation
  );

  const missingFields = applicableFields
    .filter((field) => !field.isComplete(location))
    .map((field) => field.label);

  const totalFields = applicableFields.length;
  const completedCount = totalFields - missingFields.length;
  const completionPercent = totalFields === 0 ? 100 : Math.round((completedCount / totalFields) * 100);

  return { completionPercent, missingFields, isComplete: missingFields.length === 0 };
}

